/**
 * Mapa: putanja rute -> lista dozvola (any-of semantika).
 *
 * Stranica je vidljiva ako korisnik ima BILO KOJU od navedenih dozvola.
 * Rute koje NISU u mapi su dozvoljene (prazna lista zahteva) - npr. dashboardi,
 * profil, blog, dešavanja, pomoć, podešavanja.
 *
 * Napomena: backend trenutno ne izdaje "*.view" dozvole za većinu modula,
 * zato se vidljivost stranice određuje preko akcionih dozvola.
 */
export const ROUTE_PERMISSIONS = {
  '/rooms': ['rooms.create', 'rooms.delete'],
  // Listu zaposlenih vidi i onaj kome je dodeljeno upravljanje smenama
  // (`shifts.manage`, "Dodavanje/brisanje smena za zaposlene"), ali na
  // profilu zaposlenog vidi SAMO tab „Smene".
  '/employees': ['employees.manage', 'shifts.manage'],
  '/shift-management': ['shifts.manage'],
  // „Zadaci" se primarno ograđuju prefiksom `tasks.` (vidi
  // ROUTE_PERMISSION_PREFIXES ispod); `tasks.assign` ostaje kao fallback.
  '/tasks': ['tasks.assign'],
  // Rute kojima je pristup dozvoljen svim zaposlenima (prazna lista = bez
  // zahteva). Akcije unutar stranice se ograđuju dozvolama u samoj stranici.
  '/jelovnik': [],
  '/meals': ['menus.manage', 'meals.manage'],
  // „Administracija kuhinje" (tipovi/planovi ishrane) - pristup ima i onaj ko
  // sme da dodaje/menja/briše planove ishrane (`diet-plans.manage`).
  '/kuhinja-administracija': ['menus.manage', 'meals.manage', 'diet-plans.manage'],
  '/residents': [
    'residents.create',
    'residents.update',
    'residents.deactivate',
    'residents.health.manage',
    'residents.diet.manage',
    'residents.family.manage',
    // Dokumenti i budžet su pod-stranice korisnika (profil / finansije),
    // pa njihove dozvole takođe otvaraju pristup stranici Korisnici.
    'documents.view',
    'documents.upload',
    'budget.transactions.manage',
  ],
  '/residents/groups': [
    'groups.manage',
    'groups.members.manage',
    'groups.activities.manage',
  ],
  '/activities': ['activities.manage', 'resident-activities.manage'],
};

// Rute koje zahtevaju BILO KOJU dozvolu sa datim prefiksom (umesto tačno
// nabrojanih ključeva). Prefiksno pravilo ima prednost nad listom iz
// ROUTE_PERMISSIONS.
//
// „Zadaci" (/tasks) sme da vidi samo korisnik koji ima BAR JEDNU dozvolu za
// zadatke - dodeljivanje (`tasks.assign`), pravljenje/menjanje/brisanje
// (npr. `tasks.manage`) ili pregled svih (`tasks.view`). Na ovaj način se
// pokrivaju svi akcioni ključevi sa prefiksom `tasks.` bez obzira na tačan
// naziv na backendu.
export const ROUTE_PERMISSION_PREFIXES = {
  '/tasks': 'tasks.',
};

// „Organizacija" (/settings) prikazuje podešavanja oblasti i tipova smena.
// Ruta sama nema propisanu dozvolu (vidi komentar iznad ROUTE_PERMISSIONS), pa
// je vidljivost tog taba zadana ovde - isti uslov po kome se ranije prikazivala
// stavka „Organizacija" unutar grupe „Zaposleni": administrator,
// `employees.manage`, `shifts.manage` ili bilo koja `tasks.*` dozvola.
export const ORGANIZATION_SETTINGS_PERMISSIONS = ['employees.manage', 'shifts.manage'];

/**
 * Da li korisnik sme da vidi podešavanja u okviru stranice „Organizacija"
 * (oblasti i tipovi smena).
 * @param {{ canAny: Function, permissions: Set<string>, isAdmin: boolean }} ctx
 * @returns {boolean}
 */
export const canSeeOrganizationSettings = (ctx) =>
  Boolean(ctx.isAdmin) ||
  ctx.canAny(...ORGANIZATION_SETTINGS_PERMISSIONS) ||
  canAccessPath('/tasks', ctx);

/**
 * Vraća listu dozvola potrebnih za datu putanju.
 * @param {string} path
 * @returns {string[]}
 */
export const permissionsForPath = (path) => ROUTE_PERMISSIONS[path] ?? [];

/**
 * Da li korisnik sme da pristupi/vidi datu putanju (ruta ili stavka menija).
 * Prefiksna pravila imaju prednost; inače važi any-of semantika nad listom
 * ključeva (prazna lista = bez zahteva).
 * @param {string} path
 * @param {{ canAny: Function, permissions: Set<string>, isAdmin: boolean }} ctx
 * @returns {boolean}
 */
export const canAccessPath = (path, { canAny, permissions, isAdmin }) => {
  const prefix = ROUTE_PERMISSION_PREFIXES[path];
  if (prefix) {
    return (
      isAdmin ||
      (permissions instanceof Set &&
        [...permissions].some(
          (p) => typeof p === 'string' && p.startsWith(prefix)
        ))
    );
  }

  const required = permissionsForPath(path);
  return required.length === 0 || canAny(...required);
};

export default ROUTE_PERMISSIONS;
